<!-- Top navbar for logged-out visitors on every public page, at every width (the
     homepage included). Phones get a hamburger dropdown instead of the inline links. -->
<template>
  <header class="guest-navbar sticky top-0 z-50 border-b border-border bg-background/85 backdrop-blur-md text-foreground">
    <div class="max-w-[1360px] mx-auto px-[clamp(20px,5vw,64px)] h-16 flex items-center gap-3 sm:gap-6">
      <router-link :to="home" class="flex items-center gap-2.5 shrink-0 min-h-[44px]" @click="onLogo">
        <img src="/logo/LogoIcon.png" alt="" class="w-8 h-8 rounded-lg object-cover" />
        <span class="text-lg font-extrabold tracking-[-0.02em]">Applypa</span>
      </router-link>

      <nav aria-label="Main" class="hidden lg:flex items-center gap-1 text-sm font-medium">
        <a
          v-for="item in navItems"
          :key="item.label"
          :href="item.href"
          class="px-3 py-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors whitespace-nowrap"
          @click.prevent="onNav(item)"
        >{{ item.label }}</a>
        <div v-if="tools.length" ref="toolsEl" class="relative">
          <button
            type="button"
            aria-haspopup="true"
            :aria-expanded="toolsOpen"
            class="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors whitespace-nowrap"
            @click="toolsOpen = !toolsOpen"
          >
            Free Tools
            <i class="fas fa-chevron-down text-[10px] transition-transform" :class="{ 'rotate-180': toolsOpen }" aria-hidden="true"></i>
          </button>
          <div v-if="toolsOpen" class="absolute left-0 top-full mt-2 w-80">
            <div class="rounded-xl border border-border bg-card shadow-lg p-2">
              <router-link
                v-for="tool in tools"
                :key="tool.to"
                :to="tool.to"
                class="flex items-start gap-3 rounded-lg p-3 hover:bg-accent transition-colors"
                @click="toolsOpen = false"
              >
                <span class="w-9 h-9 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  <i :class="tool.icon" aria-hidden="true"></i>
                </span>
                <span>
                  <span class="block font-semibold text-foreground">{{ tool.label }}</span>
                  <span class="block text-xs text-muted-foreground font-normal leading-snug">{{ tool.description }}</span>
                </span>
              </router-link>
            </div>
          </div>
        </div>
      </nav>

      <div class="ml-auto flex items-center gap-1 sm:gap-2">
        <router-link to="/login" class="hidden lg:inline-flex px-3 py-2 rounded-lg text-sm font-semibold hover:bg-accent transition-colors">Log in</router-link>
        <router-link
          to="/register"
          class="inline-flex items-center h-11 px-3 sm:px-4 rounded-lg text-sm font-bold bg-primary text-primary-foreground whitespace-nowrap hover:opacity-90"
        >
          Start free trial
        </router-link>
        <button
          type="button"
          :aria-label="menuOpen ? 'Close menu' : 'Open menu'"
          :aria-expanded="menuOpen"
          class="lg:hidden w-11 h-11 rounded-lg border border-border bg-card text-foreground text-base"
          @click="menuOpen = !menuOpen"
        >
          <i :class="menuOpen ? 'fas fa-xmark' : 'fas fa-bars'"></i>
        </button>
      </div>
    </div>

    <div v-if="menuOpen" class="lg:hidden border-t border-border bg-background px-4 pt-2 pb-5 flex flex-col">
      <a
        v-for="item in menuItems"
        :key="item.label"
        :href="item.href"
        class="flex items-center min-h-[48px] text-[17px] font-semibold border-b border-border"
        @click.prevent="onNav(item)"
      >{{ item.label }}</a>
      <p v-if="tools.length" class="pt-4 pb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">Free Tools</p>
      <router-link
        v-for="tool in tools"
        :key="tool.to"
        :to="tool.to"
        class="flex items-center gap-3 min-h-[48px] text-[17px] font-semibold border-b border-border"
      >
        <i :class="[tool.icon, 'text-primary w-5 text-center']" aria-hidden="true"></i>{{ tool.label }}
      </router-link>
      <div class="grid grid-cols-2 gap-2 mt-4">
        <router-link to="/login" class="flex items-center justify-center h-12 rounded-[10px] border border-border text-[15px] font-bold">Log in</router-link>
        <a :href="demoUrl" target="_blank" rel="noopener" class="flex items-center justify-center h-12 rounded-[10px] border border-border text-[15px] font-bold">Book a demo</a>
      </div>
    </div>
  </header>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';

export default {
  name: 'GuestNavbar',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const menuOpen = ref(false);
    const demoUrl = 'https://calendly.com/applypa-support/30min';

    // A landing page can set meta.navHome (e.g. /olj) so the logo and section
    // links stay on that page instead of the Upwork-focused homepage. It's
    // remembered for the tab, so Log in / Register keep the same navbar, until
    // the visitor opens the main homepage.
    const NAV_HOME_KEY = 'applypa_nav_home';
    const readNavHome = () => {
      try { return sessionStorage.getItem(NAV_HOME_KEY); } catch { return null; }
    };
    const savedHome = ref(readNavHome());
    // Also watch matched: loading "/" directly keeps path "/" while the route resolves.
    watch(() => [route.path, route.matched.length], () => {
      if (!route.matched.length) return; // initial load: the real route isn't resolved yet
      const next = route.meta?.navHome || (route.path === '/' ? null : savedHome.value);
      if (next === savedHome.value) return;
      savedHome.value = next;
      try {
        if (next) sessionStorage.setItem(NAV_HOME_KEY, next);
        else sessionStorage.removeItem(NAV_HOME_KEY);
      } catch { /* storage blocked: the navbar just falls back to the site's */ }
    }, { immediate: true });
    const home = computed(() => route.meta?.navHome
      || (route.path === '/' && route.matched.length ? '/' : savedHome.value) || '/');
    const isLanding = computed(() => home.value !== '/');

    // Pricing and FAQ are homepage sections; from other pages they go home first.
    const siteNavItems = [
      { label: 'Features', href: '/features' },
      { label: 'Pricing', href: '/#pricing', section: 'pricing' },
      { label: 'FAQ', href: '/#faq', section: 'faq' },
      { label: 'Blog', href: '/blog' },
    ];
    const navItems = computed(() => (isLanding.value
      ? [
        { label: 'How it works', href: `${home.value}#how`, section: 'how' },
        { label: 'Features', href: `${home.value}#features`, section: 'features' },
        { label: 'Pricing', href: `${home.value}#pricing`, section: 'pricing' },
        { label: 'FAQ', href: `${home.value}#faq`, section: 'faq' },
      ]
      : siteNavItems));
    const menuItems = computed(() => [...navItems.value, { label: 'Contact', href: '/contact' }]);
    // Add new free tools here; they show in the desktop dropdown and the phone menu.
    // They're Upwork tools, so landing pages hide them.
    const siteTools = [
      {
        label: 'Upwork Profile Optimizer',
        description: 'Score your profile and get a rewrite that wins more clicks.',
        to: '/tools/profile-optimizer',
        icon: 'fas fa-wand-magic-sparkles',
      },
    ];
    const tools = computed(() => (isLanding.value ? [] : siteTools));
    const toolsOpen = ref(false);
    const toolsEl = ref(null);

    function onDocumentClick(e) {
      if (toolsEl.value && !toolsEl.value.contains(e.target)) toolsOpen.value = false;
    }
    function onKeydown(e) {
      if (e.key === 'Escape') toolsOpen.value = false;
    }
    onMounted(() => {
      document.addEventListener('click', onDocumentClick);
      document.addEventListener('keydown', onKeydown);
    });
    onBeforeUnmount(() => {
      document.removeEventListener('click', onDocumentClick);
      document.removeEventListener('keydown', onKeydown);
    });

    async function onNav(item) {
      menuOpen.value = false;
      const path = item.section ? home.value : item.href;
      if (route.path !== path) await router.push(path);
      if (!item.section) return;
      // The homepage is lazy-loaded, so wait for its section to exist.
      for (let i = 0; i < 20; i += 1) {
        await nextTick();
        const el = document.getElementById(item.section);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          return;
        }
        await new Promise((r) => setTimeout(r, 50));
      }
    }

    function onLogo() {
      menuOpen.value = false;
      if (route.path === home.value) window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    watch(() => route.fullPath, () => { menuOpen.value = false; toolsOpen.value = false; });

    return { home, navItems, menuItems, tools, toolsOpen, toolsEl, menuOpen, demoUrl, onNav, onLogo };
  },
};
</script>
